import { PdfDict, PdfDocument, PdfValue } from "./pdf-objects"
import { resolveProjection, toProjected } from "./projection"

type UnitSquareFrame = [xAtU0: number, yAtV0: number, xAtU1: number, yAtV1: number]

type PageBox = [minX: number, minY: number, maxX: number, maxY: number]

type GeoControlPoint = { pageX: number; pageY: number; easting: number; northing: number }

const DEFAULT_BOUNDS = [0, 0, 0, 1, 1, 1, 1, 0]
const INHERITABLE_PAGE_KEYS = ["MediaBox", "CropBox", "Rotate"]
const MINIMUM_CONTROL_POINTS = 3
const DEGREES_PER_QUARTER_TURN = 90
const TRANSFORM_TERMS = 6

const normalize = (box: number[]) =>
    [
        Math.min(box[0], box[2]),
        Math.min(box[1], box[3]),
        Math.max(box[0], box[2]),
        Math.max(box[1], box[3]),
    ] as PageBox

const intersect = (a: PageBox, b: PageBox) =>
    [
        Math.max(a[0], b[0]),
        Math.max(a[1], b[1]),
        Math.min(a[2], b[2]),
        Math.min(a[3], b[3]),
    ] as PageBox

const findFirstPage = (doc: PdfDocument) => {
    if (!doc.root) return null
    const merged = new Map<string, PdfValue>()
    let node = doc.asDict(doc.entry(doc.root, "Pages"))

    while (node) {
        for (const key of INHERITABLE_PAGE_KEYS) {
            const value = doc.entry(node, key)
            if (value !== null) merged.set(key, value)
        }
        if (doc.asName(doc.entry(node, "Type")) === "Page") {
            for (const [key, value] of node) if (!merged.has(key)) merged.set(key, value)
            return merged as PdfDict
        }
        const kids = doc.asDicts(doc.entry(node, "Kids"))
        if (kids.length === 0) return null
        node = kids[0]
    }
    return null
}

const findGeoViewport = (doc: PdfDocument, page: PdfDict) => {
    let widest: { frame: UnitSquareFrame; measure: PdfDict } | null = null
    let widestArea = -1

    for (const viewport of doc.asDicts(doc.entry(page, "VP"))) {
        const box = doc.asNumbers(doc.entry(viewport, "BBox"))
        const measure = doc.asDict(doc.entry(viewport, "Measure"))
        if (box.length !== 4 || !measure) continue
        if (doc.asName(doc.entry(measure, "Subtype")) !== "GEO") continue

        const frame = box as UnitSquareFrame
        const area = Math.abs((frame[2] - frame[0]) * (frame[3] - frame[1]))
        if (area <= widestArea) continue
        widest = { frame, measure }
        widestArea = area
    }
    return widest
}

const readMeasureGeoreference = (doc: PdfDocument, page: PdfDict) => {
    const viewport = findGeoViewport(doc, page)
    if (!viewport) return null

    const gcs = doc.asDict(doc.entry(viewport.measure, "GCS"))
    const projection = gcs && resolveProjection(doc, gcs)
    if (!projection) return null

    const geographic = doc.asNumbers(doc.entry(viewport.measure, "GPTS"))
    const declaredUnitSquare = doc.asNumbers(doc.entry(viewport.measure, "LPTS"))
    const declaredBounds = doc.asNumbers(doc.entry(viewport.measure, "Bounds"))
    const unitSquare = declaredUnitSquare.length ? declaredUnitSquare : declaredBounds
    const linear = unitSquare.length ? unitSquare : DEFAULT_BOUNDS

    const [xAtU0, yAtV0, xAtU1, yAtV1] = viewport.frame
    const controlPointCount = Math.min(geographic.length, linear.length) >> 1
    if (controlPointCount < MINIMUM_CONTROL_POINTS) return null

    const controlPoints: GeoControlPoint[] = []
    for (let index = 0; index < controlPointCount; index++) {
        const [easting, northing] = toProjected(
            projection,
            geographic[index * 2 + 1],
            geographic[index * 2]
        )
        controlPoints.push({
            pageX: xAtU0 + linear[index * 2] * (xAtU1 - xAtU0),
            pageY: yAtV0 + linear[index * 2 + 1] * (yAtV1 - yAtV0),
            easting,
            northing,
        })
    }
    return { projection, controlPoints }
}

const readLgiGeoreference = (doc: PdfDocument, page: PdfDict) => {
    const value = doc.entry(page, "LGIDict")
    const single = doc.asDict(value)

    for (const candidate of single ? [single] : doc.asDicts(value)) {
        const transform = doc.asNumbers(doc.entry(candidate, "CTM"))
        const projectionDict = doc.asDict(doc.entry(candidate, "Projection"))
        if (transform.length !== TRANSFORM_TERMS || !projectionDict) continue

        const projection = resolveProjection(doc, projectionDict)
        if (!projection) continue

        const [eastPerX, northPerX, eastPerY, northPerY, eastOffset, northOffset] = transform
        const at = (pageX: number, pageY: number) => ({
            pageX,
            pageY,
            easting: eastPerX * pageX + eastPerY * pageY + eastOffset,
            northing: northPerX * pageX + northPerY * pageY + northOffset,
        })
        return { projection, controlPoints: [at(0, 0), at(1, 0), at(0, 1), at(1, 1)] }
    }
    return null
}

export const readGeoMeasure = (doc: PdfDocument) => {
    const page = findFirstPage(doc)
    if (!page) return null

    const georeference = readMeasureGeoreference(doc, page) ?? readLgiGeoreference(doc, page)
    if (!georeference) return null

    const mediaBox = doc.asNumbers(doc.entry(page, "MediaBox"))
    if (mediaBox.length !== 4) return null
    const cropBox = doc.asNumbers(doc.entry(page, "CropBox"))
    const rotation = doc.asNumber(doc.entry(page, "Rotate")) ?? 0

    return {
        ...georeference,
        renderedBox: intersect(
            normalize(mediaBox),
            normalize(cropBox.length === 4 ? cropBox : mediaBox)
        ),
        quarterTurnsClockwise: (((rotation / DEGREES_PER_QUARTER_TURN) % 4) + 4) % 4,
    }
}

export type GeoMeasure = NonNullable<ReturnType<typeof readGeoMeasure>>
